<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  // URL of the conversation list to return to when the pane is closed.
  closeUrl: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const contact = computed(() =>
  store.getters['contacts/getContact'](currentChat.value.meta?.sender?.id)
);

const isContactBlocked = computed(() => contact.value?.blocked);

const dropdownItems = computed(() => [
  {
    icon: isContactBlocked.value ? 'i-lucide-shield-check' : 'i-lucide-shield-x',
    label: isContactBlocked.value
      ? t('CONVERSATION.HEADER.UNBLOCK_CONTACT')
      : t('CONVERSATION.HEADER.BLOCK_CONTACT'),
    action: 'block',
    value: 'block',
  },
  {
    icon: 'i-lucide-share',
    label: t('CONVERSATION.HEADER.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  },
  {
    icon: 'i-lucide-x',
    label: t('CONVERSATION.HEADER.CLOSE_PANE'),
    action: 'close_pane',
    value: 'close_pane',
  },
]);

const toggleBlock = async () => {
  const blocked = !isContactBlocked.value;
  try {
    await store.dispatch('contacts/update', {
      ...contact.value,
      blocked,
    });
    useAlert(
      blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      blocked
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE')
    );
  }
};

// Close the active chat pane. This only clears the active conversation view
// and returns the operator to the empty "no conversation selected" state — it
// does not resolve, delete or otherwise modify the conversation.
//
// The empty state (EmptyState.vue) shows a perpetual spinner whenever either
// loading flag is stuck true:
//   • inboxes `isFetching` — the SUMO iframe fires `inboxes/get` on mount
//     (Dashboard.vue) and that request can hang and never settle → the pane
//     shows "Loading inboxes" forever.
//   • conversations `listLoadingStatus` — defaults to true and is only cleared
//     on a *successful* list fetch.
// While a conversation is open the MessagesView hides this, but closing the
// pane reveals it. Force both flags off so the operator always lands on the
// clean empty state instead of a spinner.
const closePane = () => {
  store.dispatch('clearSelectedState');
  store.commit('CLEAR_LIST_LOADING_STATUS');
  store.commit('inboxes/SET_INBOXES_UI_FLAG', { isFetching: false });
  if (props.closeUrl) {
    router.push(props.closeUrl);
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);
  if (action === 'block') {
    toggleBlock();
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'close_pane') {
    closePane();
  }
};
</script>

<template>
  <div class="relative flex items-center">
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="dropdownItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full min-w-[180px]"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
